import { cn } from '../lib/utils'

/** What an idle pip looks like: a faint track dot, or a solid green one. */
export enum ProgressPipRest {
  Track = 'track',
  Filled = 'filled'
}

export interface ProgressPipProps {
  /** Stretches the dot into a track and shows the fill. */
  active: boolean
  /** How far through the section the reader is, 0–1. Only drawn while `active`. */
  progress?: number
  /** Track length in px once active. */
  length: number
  rest?: ProgressPipRest
  className?: string
}

/** The idle dot, and the fill's floor — the fill never shrinks below a dot. */
const DOT_PX = 5

/**
 * A 5px dot that stretches into a vertical progress track when its section is the one being read.
 * Decorative — the active state must also be carried by text (weight, `aria-current`) beside it.
 */
export function ProgressPip({ active, progress = 0, length, rest = ProgressPipRest.Track, className }: ProgressPipProps) {
  const clamped = Math.min(1, Math.max(0, progress))
  return (
    <span
      aria-hidden="true"
      style={{ height: active ? length : DOT_PX }}
      className={cn(
        'relative block w-[5px] shrink-0 overflow-hidden rounded-[3px] transition-[height,margin,background-color] duration-300 ease-settle motion-reduce:transition-none',
        active || rest === ProgressPipRest.Track ? 'bg-track' : 'bg-brand-green-fill-strong',
        className
      )}>
      <span
        style={{ height: `${clamped * 100}%` }}
        className={cn(
          'absolute inset-x-0 top-0 min-h-[5px] rounded-[3px] bg-brand-green-fill-strong transition-opacity duration-200 motion-reduce:transition-none',
          active ? 'opacity-100' : 'opacity-0'
        )}
      />
    </span>
  )
}
